import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, User, Search, Menu, LogOut, ChevronDown, LifeBuoy, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const NAV_LINKS = [
  { to: "/shop", label: "SHOP" },
];

const COLLECTION_LINKS = [
  { to: "/shop", label: "New Arrivals" },
  { to: "/shop?tag=bestseller", label: "Bestsellers" },
  { to: "/shop?category=sarees&sub=cotton", label: "Pure Cotton Sarees" },
  { to: "/shop?tag=handloom", label: "Handloom" },
  { to: "/shop?tag=wedding", label: "Wedding Edit" },
];

const MOBILE_LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=sarees", label: "Sarees" },
  { to: "/shop?category=daily-wear", label: "Daily Wear" },
  { to: "/shop?category=home-essentials", label: "Home Essentials" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const doSearch = (e) => {
    e.preventDefault();

    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  const handleLogout = () => {
    navigate("/login", { replace: true, state: { loggedOut: true } });
    window.setTimeout(() => logout(), 0);
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#F7F0E4] text-[#2A1508] shadow-[0_1px_0_rgba(92,64,48,0.12)]"
    >
      <div className="nv-announcement-bar border-b border-[#D4A03A]/70">
        <div className="nv-announcement-track">
          <span className="nv-announcement-text">
            ✦ FREE SHIPPING ON ORDERS ₹1,499 AND ABOVE
            <span>✦ NEW STYLES • EVERYDAY COTTON • HERITAGE OF INDIA</span>
            ✦ FREE SHIPPING ON ORDERS ₹1,499 AND ABOVE
            <span>✦ SHOP NITYAVASTRA</span>
          </span>
          <span className="nv-announcement-text" aria-hidden="true">
            ✦ FREE SHIPPING ON ORDERS ₹1,499 AND ABOVE
            <span>✦ NEW STYLES • EVERYDAY COTTON • HERITAGE OF INDIA</span>
            ✦ FREE SHIPPING ON ORDERS ₹1,499 AND ABOVE
            <span>✦ SHOP NITYAVASTRA</span>
          </span>
        </div>
      </div>

      {/* Main navigation */}
      <div
        className="relative border-b border-[#C9A66B]/70 bg-[#FFF9EF]/95 backdrop-blur-md"
        style={{
          boxShadow: "0 1px 0 rgba(124,31,48,0.08)",
        }}
      >
        <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-5 lg:px-7 xl:px-9">
          <div className="relative h-[54px] sm:h-14 lg:h-[58px] flex items-center justify-between gap-2 sm:gap-3 lg:gap-4">

            {/* Mobile menu */}
            <div className="lg:hidden shrink-0 flex items-center">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="mobile-menu-btn"
                    className="text-[#5C4030] hover:bg-[#EBDCC8]"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>

                <SheetContent
                  side="left"
                  className="bg-[#F7F0E4] border-r-[#D8C4A8] w-[84vw] max-w-sm"
                >
                  <div className="pt-3 pb-5 border-b border-[#D8C4A8]">
                    <SheetClose asChild>
                    <Link to="/" className="flex items-center gap-3">
                      <img
                        src="https://customer-assets-lqy194kg.emergentagent.net/job_nityavastra-shop/artifacts/dirh3az1_Updated%20Logo%20Nityavastra.png"
                        alt="Nityavastra"
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-serif text-2xl text-[#4A1020]">
                          NITYAVASTRA
                        </div>
                        <div className="text-[8px] uppercase tracking-[0.2em] text-[#6B4A38]">
                          Pure Cotton Ethnic Sarees
                        </div>
                      </div>
                    </Link>
                    </SheetClose>
                  </div>

                  <nav className="flex flex-col gap-1 mt-5">
                    {MOBILE_LINKS.map((l) => (
                      <SheetClose key={l.label} asChild>
                      <Link
                        to={l.to}
                        className="flex items-center min-h-12 px-3 text-sm uppercase tracking-[0.12em] text-[#3D2118] hover:text-[#7C1F30] hover:bg-[#EBDCC8] transition-colors"
                      >
                        {l.label}
                      </Link>
                      </SheetClose>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>

            {/* Responsive left logo */}
            <Link
              to="/"
              aria-label="Nityavastra home"
              className="relative z-20 ml-0.5 shrink-0 sm:ml-1 lg:ml-0"
            >
              <img
                src="/nityavastra-logo.png"
                alt="Nityavastra"
                className="h-9 w-9 rounded-full object-contain transition-transform duration-300 hover:scale-105 sm:h-10 sm:w-10 lg:h-11 lg:w-11"
              />
            </Link>

            {/* Desktop navigation */}
            <nav className="hidden lg:flex items-center gap-5 xl:gap-7 shrink-0">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs xl:text-[13px] uppercase tracking-[0.12em] font-medium text-[#351A13] hover:text-[#7C1F30] transition-colors whitespace-nowrap"
                >
                  {l.label}
                </Link>
              ))}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-xs xl:text-[13px] uppercase tracking-[0.12em] font-medium text-[#351A13] hover:text-[#7C1F30] transition-colors"
                  >
                    COLLECTIONS
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  className="w-56 bg-[#FFF9EF] border-[#D8C4A8] p-2"
                >
                  {COLLECTION_LINKS.map((item) => (
                    <DropdownMenuItem key={item.label} asChild>
                      <Link
                        to={item.to}
                        className="cursor-pointer text-sm text-[#3D2118] focus:bg-[#F1E2CE] focus:text-[#7C1F30]"
                      >
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/about"
                className="text-xs xl:text-[13px] uppercase tracking-[0.12em] font-medium text-[#351A13] hover:text-[#7C1F30] transition-colors whitespace-nowrap"
              >
                STORY
              </Link>

            </nav>

            {/* Centered wordmark */}
            <Link
              to="/"
              data-testid="site-logo"
              className="absolute left-1/2 z-10 -translate-x-1/2 whitespace-nowrap leading-none"
            >
              <span className="font-serif text-[16px] tracking-[0.055em] text-[#541522] sm:text-[20px] md:text-[22px] lg:text-[24px] xl:text-[26px]">
                NITYAVASTRA
              </span>
            </Link>

            {/* Right actions */}
            <div className="ml-auto flex items-center gap-0 sm:gap-0.5 shrink-0 text-[#5C4030]">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchOpen((s) => !s)}
                data-testid="search-toggle-btn"
                className="hover:bg-[#EBDCC8] hover:text-[#7C1F30]"
              >
                <Search className="h-5 w-5" />
              </Button>

              <Link to="/wishlist" data-testid="nav-wishlist-link">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-[#EBDCC8] hover:text-[#7C1F30]"
                >
                  <Heart className="h-5 w-5" />
                  {wishlist.length > 0 && (
                    <span className="absolute top-0 right-0 bg-[#7C1F30] text-white text-[9px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                      {wishlist.length}
                    </span>
                  )}
                </Button>
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="user-menu-btn"
                    aria-label="Account and access"
                    className="relative hover:bg-[#EBDCC8] hover:text-[#7C1F30]"
                  >
                    <User className="h-5 w-5" />

                    {user?.role === "customer" && (
                      <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full border border-[#FFF9EF] bg-[#2E7D32]" />
                    )}
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  sideOffset={10}
                  className="w-[248px] overflow-hidden border-[#D8C4A8] bg-[#FFF9EF] p-1 shadow-xl"
                >
                  {user?.role === "customer" ? (
                    <>
                      <div className="px-2.5 pb-1.5 pt-1.5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#9A6A20]">
                          Your Nityavastra
                        </p>

                        <p className="mt-1 truncate text-sm font-semibold text-[#351A13]">
                          {user.name || "Customer"}
                        </p>

                        <p className="mt-0.5 truncate text-[11px] text-[#7C6655]">
                          {user.email}
                        </p>
                      </div>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() => navigate("/account")}
                        data-testid="menu-account"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <User className="mr-2.5 h-4 w-4 text-[#7C1F30]" />
                        <span>My Account</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => navigate("/account?tab=orders")}
                        data-testid="menu-orders"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <ShoppingBag className="mr-2.5 h-4 w-4 text-[#7C1F30]" />
                        <span>My Orders</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => navigate("/wishlist")}
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <Heart className="mr-2.5 h-4 w-4 text-[#7C1F30]" />
                        <span>Wishlist</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => navigate("/help-support")}
                        data-testid="menu-help-support"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <LifeBuoy className="mr-2.5 h-4 w-4 text-[#176963]" />
                        <span>Help & Support</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/contact")}
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <span className="mr-2.5 flex h-4 w-4 items-center justify-center text-sm text-[#7C1F30]">
                          ?
                        </span>
                        <span>Contact Us</span>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <div className="px-2.5 pb-0.5 pt-1">
                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#9A6A20]">
                          Role / Department
                        </p>
                      </div>

                      <DropdownMenuItem
                        onClick={() => navigate("/vendor/login")}
                        data-testid="menu-become-seller"
                        className="cursor-pointer rounded-md bg-[#EEF7F5] px-2.5 py-2 text-[#285E59] focus:bg-[#DDEFEA] focus:text-[#285E59]"
                      >
                        <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#14746F] text-[10px] font-bold text-white">
                          S
                        </span>

                        <div>
                          <p className="text-sm font-semibold">
                            Become a Partner
                          </p>
                          <p className="text-[10px] text-[#7C6655]">
                            Start selling on Nityavastra
                          </p>
                        </div>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={handleLogout}
                        data-testid="menu-logout"
                        className="cursor-pointer rounded-md px-3 py-2.5 text-[#A12B2B] focus:text-[#A12B2B]"
                      >
                        <LogOut className="mr-2.5 h-4 w-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </>
                  ) : user ? (
                    <>
                      <div className="px-3 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#9A6A20]">
                          Account
                        </p>

                        <p className="mt-1 text-sm font-semibold text-[#351A13]">
                          Signed in
                        </p>
                      </div>

                      <DropdownMenuSeparator />

                      {user.role === "admin" && (
                        <>
                          <DropdownMenuItem
                            onClick={() => navigate("/admin")}
                            data-testid="menu-return-admin"
                            className="cursor-pointer rounded-md px-3 py-2.5 text-[#176963] focus:bg-[#E7F0ED] focus:text-[#176963]"
                          >
                            <LayoutDashboard className="mr-2.5 h-4 w-4" />
                            Return to Admin Console
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}

                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer rounded-md px-3 py-2.5 text-[#A12B2B]"
                      >
                        <LogOut className="mr-2.5 h-4 w-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <div className="px-2.5 pb-1.5 pt-1.5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#9A6A20]">
                          Account & Access
                        </p>

                      </div>

                      <DropdownMenuSeparator />

                      <div className="px-2.5 pb-0.5 pt-1">
                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#9A6A20]">
                          Customer
                        </p>
                      </div>

                      <DropdownMenuItem
                        onClick={() => navigate("/login")}
                        data-testid="menu-customer-login"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <User className="mr-2.5 h-4 w-4 text-[#7C1F30]" />

                        <div>
                          <p className="text-sm font-medium">Sign In</p>
                          <p className="text-[10px] text-[#7C6655]">
                            Access your customer account
                          </p>
                        </div>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => navigate("/register")}
                        data-testid="menu-customer-register"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <span className="mr-2.5 flex h-4 w-4 items-center justify-center text-lg leading-none text-[#7C1F30]">
                          +
                        </span>

                        <div>
                          <p className="text-sm font-medium">Create Account</p>
                          <p className="text-[10px] text-[#7C6655]">
                            New to Nityavastra
                          </p>
                        </div>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <div className="px-2.5 pb-0.5 pt-1">
                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#9A6A20]">
                          Sell With Nityavastra
                        </p>
                      </div>

                      <DropdownMenuItem
                        onClick={() => navigate("/vendor/login")}
                        data-testid="menu-become-seller"
                        className="cursor-pointer rounded-md bg-[#EEF7F5] px-2.5 py-2 text-[#285E59] focus:bg-[#DDEFEA] focus:text-[#285E59]"
                      >
                        <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#14746F] text-[10px] font-bold text-white">
                          S
                        </span>

                        <div>
                          <p className="text-sm font-semibold">
                            Become a Partner
                          </p>
                          <p className="text-[10px] text-[#7C6655]">
                            Register your business
                          </p>
                        </div>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => navigate("/vendor/login")}
                        data-testid="menu-seller-login"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <span className="mr-2 flex h-4 w-4 items-center justify-center text-[10px] font-bold text-[#14746F]">
                          S
                        </span>

                        <div>
                          <p className="text-sm font-medium">Partner Sign In</p>
                          <p className="text-[10px] text-[#7C6655]">
                            Existing Nityavastra partner
                          </p>
                        </div>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() => navigate("/help-support")}
                        data-testid="menu-help-support"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <LifeBuoy className="mr-2.5 h-4 w-4 text-[#176963]" />
                        <span>Help & Support</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/contact")}
                        data-testid="menu-contact-us"
                        className="cursor-pointer rounded-md px-2.5 py-2"
                      >
                        <span className="mr-2.5 flex h-4 w-4 items-center justify-center text-sm text-[#7C1F30]">
                          ?
                        </span>
                        Contact Us
                      </DropdownMenuItem>

                      <div className="mt-0.5 rounded-md bg-[#F6F0E7] px-2.5 py-1.5">
                        <p className="text-[8px] leading-3 text-[#7C6655]">
                          Admin & IT access is private.
                        </p>
                      </div>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/cart" data-testid="nav-cart-link">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-[#EBDCC8] hover:text-[#7C1F30]"
                >
                  <ShoppingBag className="h-5 w-5" />
                  {cartCount > 0 && (
                    <span
                      data-testid="cart-count-badge"
                      className="absolute top-0 right-0 bg-[#7C1F30] text-white text-[9px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center"
                    >
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>
            </div>
          </div>

          {/* Search */}
          {searchOpen && (
            <form onSubmit={doSearch} className="pb-4">
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search sarees, weaves, collections..."
                className="bg-[#FFFDF8] border-[#D8C4A8] h-11 text-[#351A13] placeholder:text-[#9A806D]"
                data-testid="search-input"
              />
            </form>
          )}
        </div>

      </div>
    </header>
  );
}
